import type { RootStore } from '~/stores/root.store';
import {
    IBillingInfoRequest,
    IBillingInfoResponse,
    IRecurlyTokenRequest,
    IRecurlyUpgradeRequest,
    TPlanCode
} from '../types/membership.types';
import { MembershipAdapter } from './adapters/membership.adapter';

export class MembershipService extends MembershipAdapter {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super(rootStore.apiService);

        this.rootStore = rootStore;
    }

    async purchaseSubscription(data) {
        const { userStore } = this.rootStore;

        const accountId = userStore.currUser.account_id;
        return super.purchaseSubscription(accountId, data);
    }

    canChangePlan(selectedTierCode: TPlanCode) {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;
        return super.canChangePlan(accountId, selectedTierCode);
    }

    async previewPurchase(data: IRecurlyTokenRequest) {
        const {
            userStore: {
                currUser: { account_id }
            }
        } = this.rootStore;

        return super._previewPurchase(account_id, data);
    }

    async getPlansForPriceUpdate() {
        const {
            userStore: {
                currUser: { account_id }
            }
        } = this.rootStore;

        return super._getPlansPriceUpdate(account_id);
    }

    async getNextPayment() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super.getNextPayment(accountId);
    }

    async reactivateSubscription() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super.reactivateSubscription(accountId);
    }

    async removeSubscriptionChange() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super.removeSubscriptionChange(accountId);
    }

    async previewUpgrade(data: IRecurlyUpgradeRequest) {
        const {
            userStore: {
                currUser: { account_id }
            }
        } = this.rootStore;

        return super._previewUpgrade(account_id, data);
    }

    async changeSubscription(data: IRecurlyUpgradeRequest) {
        const {
            userStore: {
                currUser: { account_id }
            }
        } = this.rootStore;

        return super._changeSubscription(account_id, data);
    }

    async getBillingInfo() {
        const { userStore } = this.rootStore;

        const accountId = userStore.currUser.account_id;

        const {
            address: { street1, street2, region, ...address },
            ...response
        } = await super._getBillingInfo(accountId);
        const transformedResponse: IBillingInfoResponse = {
            address: {
                address1: street1,
                address2: street2,
                state: region,
                ...address
            },
            ...response
        };
        return transformedResponse;
    }

    async updateBillingInfo(data: IBillingInfoRequest) {
        const { userStore } = this.rootStore;

        return super._updateBillingInfo(userStore.currUser.account_id, data);
    }

    async updateNextPlanPeriod(data) {
        const { userStore } = this.rootStore;

        const accountId = userStore.currUser.account_id;

        return super._updateNextPlanPeriod(accountId, data);
    }

    async checkPicTimeCouponEligibility() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super._checkPicTimeCouponEligibility(accountId);
    }

    async resumePausedSubscription() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super.resumePausedSubscription(accountId);
    }

    async cancelPauseSubscription() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;
        const data = {
            other_reason: '',
            extra_info: null,
            cancellation_reasons: null,
            account_id: accountId,
            remaining_cycles: 0
        };

        return super._cancelPauseSubscription(accountId, data);
    }

    async getInvoiceHistory(begin_time: Date, end_time: Date) {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;
        return super._getInvoiceHistory(accountId, begin_time, end_time);
    }

    async getBlockedCoupons() {
        const response = await super._getBlockedCoupons();

        return (response || []).map(couponObj => couponObj.code);
    }

    async isEligibleForCoupon() {
        const { userStore } = this.rootStore;
        const accountId = userStore.currUser.account_id;

        return super.isEligibleForCoupon(accountId);
    }
}
